import { useRef } from 'react';
import constate from 'constate';
const SRC = 'zoom-provider.ts';

import { debounce } from 'debounce';
import { isTouchPad } from 'helpers/_helpers';
import { useAppDispatch } from 'store/store-hooks';
import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { setZoomTracks } from 'components/app/_mediator/app.slice';
import { swipeAccelerator, wheelAccelerator } from './zoom.helper';
import { ZoomElementType, useZoomSeek } from './use-zoom-seek';

export const WHEEL_RATIO = 1.1;
export const SWIPE_RATIO = 330;
export const MAX_ZOOM = 14;
export const WHEEL_INTERVAL_MAX = 3000;
export const WHEEL_INTERVAL_LOW = 350;

export type TrackRoot = HTMLDivElement;
export type TrackFeed = HTMLDivElement;

export type OnZoomProps = {
  event: WheelEvent;
  scrollElement: TrackRoot | null;
  zoomElementType?: ZoomElementType;
};

const VAR__RM = '--range-marker-width';
const RM_ON = 'auto';
const RM_OFF = '0';
const TRANSITION = 200; // side panel transition time, ms

const useZoomProvider = () => {
  const { ws, wsWave, wsRoot } = useAudioTrackContext();
  const { zoomSeek, resetXPos } = useZoomSeek();
  const dispatch = useAppDispatch();

  const zoomTimeout = useRef<ReturnType<typeof setTimeout>>();
  const listenElement = useRef<EventTarget | null>(null);
  const listenWheel = useRef<boolean>(false);

  const listenResizeObserver = useRef(false);
  const touchPad = useRef(false);
  const prevDate = useRef(0);
  const ratio = useRef(0);
  const zoom = useRef(1);
  const wheelClickCount = useRef(0);

  const trackElements = useRef<[TrackRoot, TrackFeed][]>([]);
  const elementsToScroll = useRef<TrackRoot[]>([]);
  const elementsToZoom = useRef<TrackFeed[]>([]);

  const rangeMarkers = {
    on: () => wsRoot.current?.style.setProperty(VAR__RM, RM_ON),
    off: () => wsRoot.current?.style.setProperty(VAR__RM, RM_OFF),
  };

  const scrollBar = {
    on: () => setTracksOverflow('auto'),
    off: () => setTracksOverflow('hidden'),
  };

  const getInterval = () => {
    const now = Date.now();
    const interval = now - prevDate.current;
    prevDate.current = now;
    return interval;
  };

  const handleWheelListener = () => {
    const cb = () => {
      listenWheel.current = false;
      rangeMarkers.on();
      dispatch(setZoomTracks(false));
      debounce(() => resetXPos(listenWheel.current), 'handleWheelListener', 800);
    };
    clearTimeout(zoomTimeout.current!);
    zoomTimeout.current = setTimeout(cb, 200);
  };

  const handleSideBar = () => {
    scrollBar.off();
    rangeMarkers.off();
    listenResizeObserver.current = true;
    setTimeout(() => {
      scrollBar.on();
      rangeMarkers.on();
      listenResizeObserver.current = false;
    }, TRANSITION + 100);
  };

  const setRatio = () => {
    if (!ws.current) return;
    ratio.current = ws.current.drawer.getWidth() / ws.current.getDuration() / ws.current.params.pixelRatio;
  };

  const setZoom = (amount: number, type: 'step' | 'value' = 'step') => {
    const value = type === 'step' ? zoom.current - amount : amount;
    zoom.current = value < 1 ? 1 : value > MAX_ZOOM ? MAX_ZOOM : value;
  };

  const setWidth = (element: HTMLDivElement) => {
    element.style.width = wsWave.current!.scrollWidth + 'px';
  };

  const setTracksOverflow = (val: 'auto' | 'hidden') => {
    if (!wsWave.current) return;
    if (zoom.current > 1) return;
    trackElements.current.forEach((el) => (el[0].style.overflow = val));
    wsWave.current.style.overflow = val;
  };

  /* *** */

  const handleZoom = () => {
    if (!ws.current) {
      console.error(`[${SRC}] handleZoom: wavesurfer is not available`);
      return;
    }
    ws.current.zoom(ratio.current * zoom.current);
    // NOTE: don't swap this sequence of actions
    elementsToZoom.current.forEach((el) => setWidth(el));
    elementsToScroll.current.forEach((el) => (el.scrollLeft = wsWave.current!.scrollLeft));
  };

  const handleScroll = (value: number) => {
    wsWave.current!.scrollLeft = value;
    elementsToScroll.current.forEach((el) => (el.scrollLeft = value));
  };

  const wheelZoom = (absY: number, deltaY: number, onZoom: OnZoomProps) => {
    if (!listenWheel.current) {
      zoomSeek(onZoom);
      rangeMarkers.off();
    }
    const step = touchPad.current
      ? swipeAccelerator(deltaY, absY)
      : wheelAccelerator(deltaY, getInterval(), wheelClickCount);
    setZoom(step);
    handleZoom();
  };

  const wheelScroll = (deltaX: number, element: TrackRoot | null) => {
    if (element) element.scrollLeft += deltaX;
    else handleScroll(wsWave.current!.scrollLeft + deltaX);
  };

  /* LISTENERS */

  const onWheel = (payload: OnZoomProps) => {
    const { event, scrollElement } = payload;
    const { deltaX, deltaY } = event;
    event.preventDefault();

    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);
    if (absX < 3 && absY < 3) return;

    if (absX > absY) wheelScroll(deltaX, scrollElement);
    else if (event.shiftKey) wheelScroll(deltaY, scrollElement);
    else wheelZoom(absY, deltaY, payload);

    if (!listenWheel.current) {
      dispatch(setZoomTracks(true));
      touchPad.current = isTouchPad(event);
      listenWheel.current = true;
    }
    handleWheelListener();
  };

  const onWaveReady = () => {
    setRatio();
  };

  const onResizeObserver = () => {
    if (!listenResizeObserver.current) return;
    onResize();
  };

  const onResize = () => {
    setTimeout(() => {
      setRatio();
      handleZoom();
    }, 0);
  };

  return {
    trackElements,
    elementsToScroll,
    elementsToZoom,

    zoomTimeout,
    handleWheelListener,
    listenElement,
    listenWheel,

    onResizeObserver,
    onWaveReady,
    onResize,
    onWheel,

    zoom,
    setZoom,
    handleZoom,
    handleScroll,
    handleSideBar,
  };
};

export const [ZoomProvider, useZoomContext] = constate(useZoomProvider);
